import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Minus, Star, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { detectGeo } from "@/lib/geo";
import { CustomPlanDialog } from "@/components/CustomPlanDialog";

const MATRIX = [
  ["Institution Profile", "Basic", "Full", "Full"],
  ["Courses", "Up to 2", "Up to 20", "Up to 50"],
  ["Course Details", "Basic", "Full", "Full"],
  ["Fees", "Basic", true, true],
  ["Eligibility", false, true, true],
  ["Specialisations", false, true, true],
  ["Website Link", true, true, true],
  ["Search Visibility", "Basic", "Enhanced", "Priority"],
  ["Featured Listing", false, false, true],
  ["Featured Course", false, false, true],
];

function Cell({ v }) {
  if (v === true) return <Check className="mx-auto h-5 w-5 text-blue-800" />;
  if (v === false) return <Minus className="mx-auto h-5 w-5 text-slate-300" />;
  return <span className="text-sm text-slate-600">{v}</span>;
}

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [geo, setGeo] = useState({ currency: "USD", symbol: "US$" });
  const [customOpen, setCustomOpen] = useState(false);
  useEffect(() => { api.get("/public/plans").then((r) => setPlans(r.data)).catch(() => {}); }, []);
  useEffect(() => { detectGeo().then(setGeo).catch(() => {}); }, []);

  const byName = Object.fromEntries(plans.map((p) => [p.name, p]));
  const cols = [["free", 1], ["standard", 2], ["premium", 3]].filter(([n]) => byName[n]);
  const isIndia = geo.currency === "INR";
  const fmt = (p) => {
    if (!p) return "";
    if (p.name === "free") return "Free";
    return isIndia
      ? `₹${p.price_inr?.toLocaleString("en-IN")}`
      : `US$ ${p.price_usd?.toLocaleString("en-US")}`;
  };
  const fmtSub = (p) => p?.name === "free" ? "" : "per year";

  const DESC = {
    free: "Get listed and build the directory",
    standard: "Enhanced information and visibility",
    premium: "Featured placement and priority",
  };
  const cards = [...plans]
    .sort((a, b) => (a.price_inr || 0) - (b.price_inr || 0))
    .map((p) => ({
      key: p.name, label: p.label || p.name,
      desc: DESC[p.name] || (p.max_courses ? `Up to ${p.max_courses} courses` : "Custom listing plan"),
      accent: p.name === "premium" ? "border-sky-300 ring-1 ring-sky-100"
        : p.name === "standard" ? "border-blue-200 ring-1 ring-blue-100" : "border-slate-200",
      featured: p.name === "premium",
    }));

  return (
    <div className="mx-auto max-w-6xl px-4 py-12 md:px-6">
      <div className="text-center">
        <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900 md:text-4xl">Listing Plans</h1>
        <p className="mx-auto mt-3 max-w-2xl text-slate-500">
          Both free and paid institutions appear in search. Paid plans provide more information, enhanced visibility and featured placement.
        </p>
      </div>

      <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map((c) => {
          const p = byName[c.key];
          return (
            <div key={c.key} data-testid={`plan-card-${c.key}`} className={`relative flex flex-col rounded-2xl border bg-white p-6 ${c.accent}`}>
              {c.featured && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-sky-600 px-3 py-1 text-xs font-semibold text-white">
                  <Star className="mr-1 inline h-3 w-3 fill-current" /> Most Visibility
                </span>
              )}
              <h3 className="font-heading text-xl font-bold text-slate-900">{c.label}</h3>
              <p className="mt-1 text-sm text-slate-500">{c.desc}</p>
              <p className="mt-4 font-heading text-3xl font-bold text-slate-900">{fmt(p)}</p>
              <p className="text-sm text-slate-400">{fmtSub(p)}</p>
              <ul className="mt-5 flex-1 space-y-2">
                {(p?.features || []).map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-600">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-blue-800" /> {f}
                  </li>
                ))}
              </ul>
              <Button asChild className="mt-6 bg-blue-800 hover:bg-blue-900" data-testid={`choose-plan-${c.key}`}>
                <Link to="/register">Choose {c.label}</Link>
              </Button>
            </div>
          );
        })}

        <div data-testid="plan-card-custom" className="relative flex flex-col rounded-2xl border border-amber-300 bg-amber-50/40 p-6">
          <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-amber-500 px-3 py-1 text-xs font-semibold text-white">
            <Sparkles className="mr-1 inline h-3 w-3" /> 50+ courses
          </span>
          <h3 className="font-heading text-xl font-bold text-slate-900">Custom Listing Plan</h3>
          <p className="mt-1 text-sm text-slate-500">For institutions listing more than 50 courses</p>
          <p className="mt-4 font-heading text-3xl font-bold text-slate-900">Let's talk</p>
          <p className="text-sm text-slate-400">tailored pricing</p>
          <ul className="mt-5 flex-1 space-y-2">
            {["Unlimited / high course volume", "Everything in Premium", "Priority featured placement", "Dedicated support"].map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm text-slate-600"><Check className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /> {f}</li>
            ))}
          </ul>
          <Button className="mt-6 bg-amber-500 text-white hover:bg-amber-600" onClick={() => setCustomOpen(true)} data-testid="choose-plan-custom">
            Request Custom Plan
          </Button>
        </div>
      </div>

      <CustomPlanDialog open={customOpen} onOpenChange={setCustomOpen} />

      {cols.length > 0 && <div className="mt-14 overflow-x-auto">
        <table className="w-full min-w-[600px] border-collapse overflow-hidden rounded-xl border border-slate-200 text-left">
          <thead>
            <tr className="bg-slate-50">
              <th className="p-4 font-heading text-sm font-semibold text-slate-900">Feature</th>
              {cols.map(([n]) => <th key={n} className="p-4 text-center font-heading text-sm font-semibold text-slate-900">{byName[n].label || n}</th>)}
            </tr>
          </thead>
          <tbody>
            {MATRIX.map((row) => (
              <tr key={row[0]} className="border-t border-slate-100">
                <td className="p-4 text-sm font-medium text-slate-700">{row[0]}</td>
                {cols.map(([n, i]) => <td key={n} className="p-4 text-center"><Cell v={row[0] === "Courses" ? (byName[n].max_courses ? `Up to ${byName[n].max_courses}` : "Unlimited") : row[i]} /></td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>}
    </div>
  );
}
